import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { MAX_PAGE_LIMIT } from '@constants/listFetching';
import { MsOrder } from './ms-order';
import { MsB2b } from './ms-b2b';
import { MsCatalog } from './ms-catalog';
import { MsAccount } from './ms-account';

// Projection for the customer list (Mongo + ES) — only what the list renders.
const CUSTOMER_LIST_FIELDS =
  '_id name status is_active image customer_type phone email market_type sales_team state city country';

const hasFilter = (filter) => !!filter && Object.keys(filter).length > 0;
const capLimit = (limit) => Math.min(limit ?? 10, MAX_PAGE_LIMIT);

// All ms-* service fns take ({ params: {...} }) — axios passes them as query params.
const runFn = async (fn, paramsObj) => {
  const res = await fn({ params: paramsObj });
  return res?.data; // { success, data, ... } envelope
};

const toError = (error) => ({
  error: { status: error?.response?.status, data: error?.message },
});

// Omit `filter` entirely when it's empty, so we never send `filter={}` on the wire.
const buildParams = (mergedFilter, rest) =>
  Object.keys(mergedFilter).length > 0
    ? { ...rest, filter: mergedFilter }
    : rest;

/**
 * Generate the three list endpoints (list / count / infinite) for one resource.
 *
 * Bakes in every convention so it can't be re-typed wrong per endpoint:
 *  - transform INLINE in queryFn (transformResponse is a no-op for queryFn endpoints)
 *  - unwrap the `{ data }` envelope and map `_id → id`
 *  - capLimit on every paginated call
 *  - Mongo↔ES switch keyed on hasFilter (only when esListFn/esCountFn are supplied)
 *  - per-resource defaultFilter / defaultSort
 *
 * @param build  the createApi `build` helper
 * @param resource  PascalCase name → endpoints get{Resource}List / Count / InfiniteList
 * @param listFn,countFn  Mongo service fns (required)
 * @param esListFn,esCountFn  ES service fns (optional → enables the switch)
 * @param defaultFilter  merged under the incoming filter (e.g. { is_deleted: false })
 * @param defaultSort  fallback sort when the arg has none (e.g. '-_id')
 */
const makeListEndpoints = (
  build,
  {
    resource,
    listFn,
    countFn,
    esListFn,
    esCountFn,
    defaultFilter = {},
    defaultSort,
  },
) => {
  const useEs = !!esListFn;
  const pickList = (filter) => (useEs && hasFilter(filter) ? esListFn : listFn);
  const pickCount = (filter) =>
    useEs && hasFilter(filter) ? esCountFn : countFn;
  const toRows = (envelope) =>
    (envelope?.data || []).map((it) => ({ ...it, id: it._id }));

  return {
    [`get${resource}List`]: build.query({
      async queryFn({ filter, skip = 0, limit, sort }) {
        try {
          const mergedFilter = { ...defaultFilter, ...filter };
          const envelope = await runFn(
            pickList(filter),
            buildParams(mergedFilter, {
              skip,
              limit: capLimit(limit),
              sort: sort || defaultSort,
            }),
          );
          return { data: toRows(envelope) };
        } catch (error) {
          return toError(error);
        }
      },
    }),

    [`get${resource}Count`]: build.query({
      async queryFn({ filter }) {
        try {
          const mergedFilter = { ...defaultFilter, ...filter };
          const envelope = await runFn(
            pickCount(filter),
            buildParams(mergedFilter, {}),
          );
          return { data: envelope?.data ?? 0 };
        } catch (error) {
          return toError(error);
        }
      },
    }),

    [`get${resource}InfiniteList`]: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam,
          allPageParams,
          queryArg,
        ) => {
          const limit = capLimit(queryArg.limit);
          return (lastPage?.length ?? 0) < limit
            ? undefined
            : lastPageParam + limit;
        },
      },
      async queryFn({ queryArg, pageParam }) {
        try {
          const mergedFilter = { ...defaultFilter, ...queryArg.filter };
          const envelope = await runFn(
            pickList(queryArg.filter),
            buildParams(mergedFilter, {
              sort: queryArg.sort || defaultSort,
              skip: pageParam,
              limit: capLimit(queryArg.limit),
            }),
          );
          return { data: toRows(envelope) };
        } catch (error) {
          return toError(error);
        }
      },
    }),
  };
};

export const listApi = createApi({
  reducerPath: 'listApi',
  // All endpoints use queryFn (calling existing ms-* service fns), so baseQuery is
  // never invoked — fakeBaseQuery satisfies createApi and throws if ever misused.
  baseQuery: fakeBaseQuery(),
  endpoints: (build) => ({
    ...makeListEndpoints(build, {
      resource: 'SalesOrder',
      listFn: MsOrder.getSalesOrder,
      countFn: MsOrder.getSalesOrderCount,
      esListFn: MsB2b.getSalesOrder,
      esCountFn: MsB2b.searchSalesOrderCount,
      defaultSort: '-created_at',
    }),
    // Home-dashboard entry (isHome). Unfiltered → the dashboard aggregation endpoint;
    // once a filter/search is applied the factory's hasFilter switch routes to ES
    // search (matching the legacy "dashboard until you search" flip). The is_dashboard
    // flag is added by the screen's toFilter on the filtered path.
    ...makeListEndpoints(build, {
      resource: 'SalesOrderDashboard',
      listFn: (arg) => MsB2b.getDashboard(arg, 'orders'),
      countFn: (arg) => MsB2b.getDashboardCount(arg, 'orders'),
      esListFn: MsB2b.getSalesOrder,
      esCountFn: MsB2b.searchSalesOrderCount,
      defaultSort: '-created_at',
    }),
    // Sales-invoice dashboard entry (isSalesInvoiceDashboard). Same shape, dashboard
    // type 'sales-invoice'; is_sales_invoice flag added by toFilter on the ES path.
    ...makeListEndpoints(build, {
      resource: 'SalesOrderSalesInvoiceDashboard',
      listFn: (arg) => MsB2b.getDashboard(arg, 'sales-invoice'),
      countFn: (arg) => MsB2b.getDashboardCount(arg, 'sales-invoice'),
      esListFn: MsB2b.getSalesOrder,
      esCountFn: MsB2b.searchSalesOrderCount,
      defaultSort: '-created_at',
    }),
    ...makeListEndpoints(build, {
      resource: 'Brand',
      listFn: MsCatalog.getBrands,
      countFn: MsCatalog.getBrandCount,
      defaultFilter: { is_deleted: false },
      defaultSort: '-_id',
    }),
    ...makeListEndpoints(build, {
      resource: 'SkpInternal',
      listFn: MsOrder.getSkpInternal,
      countFn: MsOrder.getSkpInternalCount,
      esListFn: MsB2b.getSkpInternal,
      esCountFn: MsB2b.getSkpInternalCount,
      defaultSort: '-_id',
    }),
    ...makeListEndpoints(build, {
      resource: 'MatrixApproval',
      listFn: MsB2b.getMatrixApproval,
      countFn: MsB2b.getMatrixApprovalCount,
      defaultFilter: { is_deleted: false },
      defaultSort: '-_id',
    }),
    ...makeListEndpoints(build, {
      resource: 'Users',
      listFn: MsAccount.getUsers,
      countFn: MsAccount.getUsersCount,
      defaultSort: '-_id',
    }),
    ...makeListEndpoints(build, {
      resource: 'RequestProductListing',
      listFn: MsOrder.getProductListing,
      countFn: MsOrder.getProductListingCount,
      defaultSort: '-created_at',
    }),
    ...makeListEndpoints(build, {
      resource: 'Lpb',
      listFn: MsOrder.get3plLpb,
      countFn: MsOrder.get3plLpbCount,
      defaultSort: '-created_at',
    }),
    ...makeListEndpoints(build, {
      resource: 'ReturnOrder',
      listFn: MsOrder.getReturnOrder,
      countFn: MsOrder.getReturnOrderCount,
      esListFn: MsB2b.searchReturnOrder,
      esCountFn: MsB2b.searchReturnOrderCount,
      defaultSort: '-created_at',
    }),
    ...makeListEndpoints(build, {
      resource: 'Inventory',
      listFn: MsOrder.getInventory,
      countFn: MsOrder.getInventoryCount,
      defaultSort: '-created_at',
    }),
    // Search-only list (no filter modal); legacy set no sort, so no defaultSort here.
    ...makeListEndpoints(build, {
      resource: 'InventorySummary',
      listFn: MsOrder.getInventorySummary,
      countFn: MsOrder.getInventorySummaryCount,
    }),
    ...makeListEndpoints(build, {
      resource: 'SkpExternal',
      listFn: MsOrder.getSkpExternal,
      countFn: MsOrder.getSkpExternalCount,
      esListFn: MsB2b.getSkpExternal,
      esCountFn: MsB2b.getSkpExternalCount,
      defaultSort: '-_id',
    }),
    // Sub-Distributor outlets — search-only list (no filter modal, no ES).
    ...makeListEndpoints(build, {
      resource: 'Subdistributor',
      listFn: MsAccount.getOutlets,
      countFn: MsAccount.getOutletsCount,
      defaultSort: '-created_at',
    }),
    // Request Product (tester) — Mongo-only list with filter modal + export.
    ...makeListEndpoints(build, {
      resource: 'RequestProductTester',
      listFn: MsOrder.getRequestProductTester,
      countFn: MsOrder.getRequestProductTesterCount,
      defaultSort: '-created_at',
    }),
    // Customers — Mongo list, flips to ES search once any filter/search applies.
    // `fields` scopes the projection to what the list renders — avoids pulling
    // full customer docs (large store/delivery/invoice_address arrays).
    ...makeListEndpoints(build, {
      resource: 'Customer',
      listFn: (a) =>
        MsAccount.getCustomers({
          params: { ...a.params, fields: CUSTOMER_LIST_FIELDS },
        }),
      countFn: MsAccount.getCustomersCount,
      esListFn: (a) =>
        MsB2b.getCustomersFromEs({
          params: { ...a.params, fields: CUSTOMER_LIST_FIELDS },
        }),
      esCountFn: MsB2b.getCustomersCountFromEs,
      defaultSort: '-_id',
    }),
    // Promotion rules — Mongo-only list with filter modal + export.
    ...makeListEndpoints(build, {
      resource: 'PromotionRules',
      listFn: MsOrder.getPromotionRules,
      countFn: MsOrder.getPromotionRulesCount,
      defaultSort: '-_id',
    }),
    // Pricelist (product price rules) — Mongo-only list with filter modal.
    ...makeListEndpoints(build, {
      resource: 'Pricelist',
      listFn: MsCatalog.getPriceRules,
      countFn: MsCatalog.getPriceRulesCount,
      defaultSort: '-_id',
    }),
    // Account Group — market types scoped to accounts (search-only, Mongo).
    ...makeListEndpoints(build, {
      resource: 'AccountGroup',
      listFn: MsB2b.getMarketType,
      countFn: MsB2b.getMarketTypeCount,
      defaultFilter: { is_account: true },
    }),
    // Branch List — search-only, Mongo.
    ...makeListEndpoints(build, {
      resource: 'Branch',
      listFn: MsAccount.getBranches,
      countFn: MsAccount.getBranchesCount,
    }),
    // Budget Pocket — search-only list with export modal, Mongo.
    ...makeListEndpoints(build, {
      resource: 'BudgetPocket',
      listFn: MsOrder.getBudgetPocket,
      countFn: MsOrder.getBudgetPocketCount,
      defaultSort: '-_id',
    }),
    // CMS — Mongo-only list (title search + page-type filter).
    ...makeListEndpoints(build, {
      resource: 'Cms',
      listFn: MsCatalog.getCMS,
      countFn: MsCatalog.getCMSCount,
    }),
    // COA Tax Setting — Mongo list, flips to ES search once filtered.
    ...makeListEndpoints(build, {
      resource: 'CoaTax',
      listFn: MsOrder.getCoaTaxSettings,
      countFn: MsOrder.getCoaTaxSettingsCount,
      esListFn: MsB2b.getCoaTaxes,
      esCountFn: MsB2b.getCoaTaxesCount,
      defaultSort: '-_id',
    }),
    // Credit Limit — search-only, Mongo (web-only screen).
    ...makeListEndpoints(build, {
      resource: 'CreditLimit',
      listFn: MsAccount.getCreditLimit,
      countFn: MsAccount.getCreditLimitCount,
      defaultSort: '-_id',
    }),
    // Customer Cluster — Mongo (web list, mobile search).
    ...makeListEndpoints(build, {
      resource: 'CustomerCluster',
      listFn: MsAccount.getCustomerClusterRule,
      countFn: MsAccount.getCustomerClusterRuleCount,
    }),
    // Customer Type — search + status filter, Mongo.
    ...makeListEndpoints(build, {
      resource: 'CustomerType',
      listFn: MsAccount.getCustomerTypes,
      countFn: MsAccount.getCustomerTypeCount,
    }),
    // Planogram — search by account group, Mongo.
    ...makeListEndpoints(build, {
      resource: 'Planogram',
      listFn: MsB2b.getPlanogram,
      countFn: MsB2b.getPlanogramCount,
    }),
    // Sales Team — search + market-type filter, Mongo (is_show_all baked in).
    ...makeListEndpoints(build, {
      resource: 'SalesTeam',
      listFn: MsB2b.getSalesTeam,
      countFn: MsB2b.getSalesTeamCount,
      defaultFilter: { is_show_all: true },
    }),
    // Market & Business Type — market types (search by code); rows are flattened
    // per business type in the screen via convertMarketTypeData.
    ...makeListEndpoints(build, {
      resource: 'MarketBusinessType',
      listFn: MsB2b.getMarketType,
      countFn: MsB2b.getMarketTypeCount,
    }),
    // Best Seller / New Arrival — search by title/name, Mongo (MsCatalog).
    ...makeListEndpoints(build, {
      resource: 'NewArrivalBestSeller',
      listFn: MsCatalog.getNewArrivalBestSeller,
      countFn: MsCatalog.getNewArrivalBestSellerCount,
    }),
    // Promotion Account — search by name, newest first, Mongo.
    ...makeListEndpoints(build, {
      resource: 'PromotionAccount',
      listFn: MsB2b.getPromotionAccounts,
      countFn: MsB2b.getPromotionAccountsCount,
      defaultSort: '-created_at',
    }),
    // Forecast Stock (sales teams) — search by name + year filter (year lives in
    // the filter). `fields: 'name'` scopes the backend search field; inject it on
    // both the list and count calls. Web-only screen.
    ...makeListEndpoints(build, {
      resource: 'ForecastStock',
      listFn: (a) =>
        MsOrder.getForecastSalesTeam({
          params: { ...a.params, fields: 'name' },
        }),
      countFn: (a) =>
        MsOrder.getForecastSalesTeamCount({
          params: { ...a.params, fields: 'name' },
        }),
    }),
    // Request Product Period — periods (years) list; no search/filter, Mongo.
    ...makeListEndpoints(build, {
      resource: 'RequestProductPeriod',
      listFn: MsOrder.getRequestProductPeriod,
      countFn: MsOrder.getRequestProductPeriodCount,
    }),
    // SKP External related list — external SKPs under a given internal SKP
    // (parent skp_internal.id is always in the filter). Mongo-only (NOT the ES
    // switch of the SkpExternal browse list), keyword $or + status handled by the
    // screen's toFilter.
    ...makeListEndpoints(build, {
      resource: 'SkpExternalRelated',
      listFn: MsOrder.getSkpExternal,
      countFn: MsOrder.getSkpExternalCount,
      defaultSort: '-_id',
    }),
  }),
});

export const {
  useGetSalesOrderListQuery,
  useGetSalesOrderCountQuery,
  useGetSalesOrderInfiniteListInfiniteQuery,
  useGetSalesOrderDashboardListQuery,
  useGetSalesOrderDashboardCountQuery,
  useGetSalesOrderDashboardInfiniteListInfiniteQuery,
  useGetSalesOrderSalesInvoiceDashboardListQuery,
  useGetSalesOrderSalesInvoiceDashboardCountQuery,
  useGetSalesOrderSalesInvoiceDashboardInfiniteListInfiniteQuery,
  useGetBrandListQuery,
  useGetBrandCountQuery,
  useGetBrandInfiniteListInfiniteQuery,
  useGetSkpInternalListQuery,
  useGetSkpInternalCountQuery,
  useGetSkpInternalInfiniteListInfiniteQuery,
  useGetMatrixApprovalListQuery,
  useGetMatrixApprovalCountQuery,
  useGetMatrixApprovalInfiniteListInfiniteQuery,
  useGetUsersListQuery,
  useGetUsersCountQuery,
  useGetUsersInfiniteListInfiniteQuery,
  useGetRequestProductListingListQuery,
  useGetRequestProductListingCountQuery,
  useGetRequestProductListingInfiniteListInfiniteQuery,
  useGetLpbListQuery,
  useGetLpbCountQuery,
  useGetLpbInfiniteListInfiniteQuery,
  useGetReturnOrderListQuery,
  useGetReturnOrderCountQuery,
  useGetReturnOrderInfiniteListInfiniteQuery,
  useGetInventoryListQuery,
  useGetInventoryCountQuery,
  useGetInventoryInfiniteListInfiniteQuery,
  useGetInventorySummaryListQuery,
  useGetInventorySummaryCountQuery,
  useGetInventorySummaryInfiniteListInfiniteQuery,
  useGetSkpExternalListQuery,
  useGetSkpExternalCountQuery,
  useGetSkpExternalInfiniteListInfiniteQuery,
  useGetSubdistributorListQuery,
  useGetSubdistributorCountQuery,
  useGetSubdistributorInfiniteListInfiniteQuery,
  useGetRequestProductTesterListQuery,
  useGetRequestProductTesterCountQuery,
  useGetRequestProductTesterInfiniteListInfiniteQuery,
  useGetCustomerListQuery,
  useGetCustomerCountQuery,
  useGetCustomerInfiniteListInfiniteQuery,
  useGetPromotionRulesListQuery,
  useGetPromotionRulesCountQuery,
  useGetPromotionRulesInfiniteListInfiniteQuery,
  useGetPricelistListQuery,
  useGetPricelistCountQuery,
  useGetPricelistInfiniteListInfiniteQuery,
  useGetAccountGroupListQuery,
  useGetAccountGroupCountQuery,
  useGetAccountGroupInfiniteListInfiniteQuery,
  useGetBranchListQuery,
  useGetBranchCountQuery,
  useGetBranchInfiniteListInfiniteQuery,
  useGetBudgetPocketListQuery,
  useGetBudgetPocketCountQuery,
  useGetBudgetPocketInfiniteListInfiniteQuery,
  useGetCmsListQuery,
  useGetCmsCountQuery,
  useGetCmsInfiniteListInfiniteQuery,
  useGetCoaTaxListQuery,
  useGetCoaTaxCountQuery,
  useGetCoaTaxInfiniteListInfiniteQuery,
  useGetCreditLimitListQuery,
  useGetCreditLimitCountQuery,
  useGetCreditLimitInfiniteListInfiniteQuery,
  useGetCustomerClusterListQuery,
  useGetCustomerClusterCountQuery,
  useGetCustomerClusterInfiniteListInfiniteQuery,
  useGetCustomerTypeListQuery,
  useGetCustomerTypeCountQuery,
  useGetCustomerTypeInfiniteListInfiniteQuery,
  useGetPlanogramListQuery,
  useGetPlanogramCountQuery,
  useGetPlanogramInfiniteListInfiniteQuery,
  useGetSalesTeamListQuery,
  useGetSalesTeamCountQuery,
  useGetSalesTeamInfiniteListInfiniteQuery,
  useGetMarketBusinessTypeListQuery,
  useGetMarketBusinessTypeCountQuery,
  useGetMarketBusinessTypeInfiniteListInfiniteQuery,
  useGetNewArrivalBestSellerListQuery,
  useGetNewArrivalBestSellerCountQuery,
  useGetNewArrivalBestSellerInfiniteListInfiniteQuery,
  useGetPromotionAccountListQuery,
  useGetPromotionAccountCountQuery,
  useGetPromotionAccountInfiniteListInfiniteQuery,
  useGetForecastStockListQuery,
  useGetForecastStockCountQuery,
  useGetForecastStockInfiniteListInfiniteQuery,
  useGetRequestProductPeriodListQuery,
  useGetRequestProductPeriodCountQuery,
  useGetRequestProductPeriodInfiniteListInfiniteQuery,
  useGetSkpExternalRelatedListQuery,
  useGetSkpExternalRelatedCountQuery,
  useGetSkpExternalRelatedInfiniteListInfiniteQuery,
} = listApi;
